import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  Container,
  CircularProgress,
} from "@mui/material";

import TabletIcon from "../../../Assets/Icons/tablet-icon.png";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";
import httpclient from "../../../Utils";
import { isEmptyObject } from "../../../Utils/utils";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const bayLocationInitialFields = {
  location: "",
  zone: "",
  level: "",
  sequenceNumber: "",
};

function AddBayLocation() {
  const [tabIndex, setTabIndex] = useState(0);

  const [bayLocationInfo, setBayLocationInfo] = useState(
    bayLocationInitialFields
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const location = useLocation();
  const { bayLocation } = location.state || {};
  const hasPDevice = !isEmptyObject(bayLocation);

  const [mode, setMode] = useState(hasPDevice ? "edit" : "add");

  const navigate = useNavigate();
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  useEffect(() => {
    if (mode === "edit") {
      const { location, zone, level, sequence_number } = bayLocation;

      setBayLocationInfo({
        location: location,
        zone: zone,
        level: level,
        sequenceNumber: sequence_number,
      });
      setMode("edit");
    } else {
      setMode("add");
    }
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleBayLocationInfoChange = (event) => {
    const { name, value } = event.target;

    setBayLocationInfo((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const rules = [
      // { condition: !bayLocationInfo.name, message: "Location is required" },
    ];

    for (const rule of rules) {
      if (rule.condition) {
        showNotification("error", rule.message);
        return false;
      }
    }
    return true;
  };

  const getFormattedData = (data) => {
    const formData = new FormData();

    const dataMap = {
      location: data.location,
      level: data.level,
      sequence_number: data.sequenceNumber,
    };

    Object.entries(dataMap).forEach(([key, value]) => {
      formData.append(key, value);
    });

    return formData;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const endpoint =
      mode === "edit"
        ? `/bin-bay-location/${bayLocation.id}`
        : "/bin-bay-location";

    try {
      setIsSubmitting(true);
      const bayLocationData = getFormattedData(bayLocationInfo);

      const res = await httpclient.post(endpoint, bayLocationData);

      const { data } = res;

      if (data.status === 200) {
        showNotification("success", data.message);

        navigate("/bin-bay-location");
      } else {
        showNotification("error", data.message);
      }
    } catch (error) {
      handleApiError(error, setIsSubmitting);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setBayLocationInfo(bayLocationInitialFields);

    setTimeout(() => {
      navigate("/bin-bay-location");
    }, 100);
  };

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ overflow: "hidden" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="bin bay location detail tabs"
          >
            <Tab
              icon={<img src={TabletIcon} alt="Tablet icon" />}
              iconPosition="start"
              label="Bin Bay Location Detail"
              id="tab-0"
              aria-controls="tabpanel-0"
              sx={{
                "&.Mui-selected": { color: "#7152F3" },
              }}
            />
          </Tabs>
        </Box>

        <TabPanel value={tabIndex} index={0}>
          <Box component="form" onSubmit={handleSave} noValidate>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="location"
                  label="Location"
                  name="location"
                  value={bayLocationInfo.location}
                  onChange={handleBayLocationInfoChange}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="zone"
                  label="Zone"
                  name="zone"
                  value={bayLocationInfo.zone}
                  onChange={handleBayLocationInfoChange}
                  variant="outlined"
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="level"
                  label="Level"
                  name="level"
                  value={bayLocationInfo.level}
                  onChange={handleBayLocationInfoChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="sequenceNumber"
                  label="Sequence Number"
                  name="sequenceNumber"
                  value={bayLocationInfo.sequenceNumber}
                  onChange={handleBayLocationInfoChange}
                  variant="outlined"
                />
              </Grid>

              <Grid
                item
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{ mr: 2, textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  sx={{ backgroundColor: "#7152F3", textTransform: "none" }}
                  disabled={isSubmitting}
                >
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        marginTop: "-12px",
                        marginLeft: "-12px",
                      }}
                    />
                  )}
                  {mode === "edit" ? "Update" : "Save"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
}

export default AddBayLocation;
